import React, { useEffect, useState, useMemo } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaTrophy, FaUsers, FaCalendarAlt, FaMedal, FaHandshake } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import './Achievements.css';

interface Achievement {
  icon: React.ReactNode;
  number: number;
  label: string;
  suffix?: string;
  color: string;
}

const Achievements: React.FC = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });
  const [counters, setCounters] = useState<number[]>([0, 0, 0, 0]);
  const [isMobile, setIsMobile] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const achievements: Achievement[] = useMemo(() => [
    {
      icon: <FaUsers />,
      number: 100,
      label: t('achievements.stats.activePlayers'),
      suffix: '+',
      color: 'var(--junior-gold)'
    },
    {
      icon: <FaTrophy />,
      number: 30,
      label: t('achievements.stats.trophiesWon'),
      suffix: '+',
      color: 'var(--junior-red)'
    },
    {
      icon: <FaCalendarAlt />,
      number: 20,
      label: t('achievements.stats.yearsExcellence'),
      color: 'var(--junior-blue)'
    },
    {
      icon: <FaMedal />,
      number: 5,
      label: t('achievements.stats.proCoaches'),
      color: 'var(--junior-gold)'
    }
  ], [t]);

  useEffect(() => {
    if (isInView && !isMobile) {
      achievements.forEach((achievement, index) => {
        let start = 0;
        const end = achievement.number;
        const duration = 2000; // 2 seconds
        const stepTime = 50; // Update every 50ms
        const steps = duration / stepTime;
        const increment = end / steps;

        const timer = setInterval(() => {
          start += increment;
          if (start >= end) {
            start = end;
            clearInterval(timer);
          }
          setCounters(prev => {
            const newCounters = [...prev];
            newCounters[index] = Math.floor(start);
            return newCounters;
          });
        }, stepTime);

        return () => clearInterval(timer);
      });
    } else if (isInView && isMobile) {
      // On mobile, set counters immediately without animation
      setCounters(achievements.map(a => a.number));
    }
  }, [isInView, isMobile, achievements]);

  return (
    <section id="achievements" className="achievements section-lg" ref={ref}>
      <div className="container">
        <motion.div
          className="achievements__header"
          initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="achievements__header-mark" aria-hidden />
          <h2 className="achievements__title">{t('achievements.title')}</h2>
          <p className="achievements__subtitle">{t('achievements.subtitle')}</p>
        </motion.div>

        <div className="achievements__grid">
          {achievements.map((achievement, index) => (
            <motion.div
              key={index}
              className="achievement-card"
              initial={isMobile ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 50, scale: 0.9 }}
              whileInView={isMobile ? { opacity: 1, y: 0, scale: 1 } : { opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={isMobile ? { y: 0 } : { y: -6 }}
            >
              <span className="achievement-card__glow" aria-hidden />
              <div className="achievement-card__inner">
                <div
                  className="achievement-card__icon"
                  style={{ color: achievement.color }}
                >
                  {achievement.icon}
                </div>
                <div className="achievement-card__content">
                  <motion.span
                    className="achievement-card__number"
                    style={{ color: achievement.color }}
                  >
                    {counters[index]}
                    {achievement.suffix || ''}
                  </motion.span>
                  <span className="achievement-card__label">{achievement.label}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="achievements__highlights"
          initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <span className="achievements__highlights-mark" aria-hidden />
          <h3 className="achievements__highlights-title">{t('achievements.highlights.title')}</h3>
          <div className="highlights-grid">
            <div className="highlight-item">
              <span className="highlight-item__glow" aria-hidden />
              <div className="highlight-item__inner">
                <div className="highlight-item__icon">
                  <FaHandshake />
                </div>
                <div className="highlight-item__content">
                  <h4>{t('achievements.highlights.academy.title')}</h4>
                  <p>{t('achievements.highlights.academy.description')}</p>
                </div>
              </div>
            </div>
            <div className="highlight-item">
              <span className="highlight-item__glow" aria-hidden />
              <div className="highlight-item__inner">
                <div className="highlight-item__icon">
                  <FaUsers />
                </div>
                <div className="highlight-item__content">
                  <h4>{t('achievements.highlights.development.title')}</h4>
                  <p>{t('achievements.highlights.development.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Achievements;
